// ════════════════════════════════════════════════════════════════
// GSA_MYRA_MESSAGES.js
// All Myra-facing text, organized by GSA step.
// Reading top-to-bottom mirrors the GSA flow.
// ════════════════════════════════════════════════════════════════

export const MYRA_MESSAGES = {

  // ── 1. INTRO ──────────────────────────────────────────────────
  intro: {
    welcome: "Welcome to Virdio Health’s Guided Self-Assessment to better understand your muscle & joint pain. In about 10 minutes, you’ll answer a few questions, complete simple movement checks, and get a personalized recovery plan you can do at home.",
    readyPrompt: "I’m Myra, your AI recovery coach. I’ll guide you step by step through this assessment using evidence-based physical therapy principles informed by Doctors of Physical Therapy.",

    // CTA response: "Who is Myra?"
    whoIsMyra: "I’m Myra, your recovery coach from Virdio Health. I’m powered by AI and designed to guide you through a recovery process based on best practices in physical therapy. My goal is to help you feel better by creating a plan tailored to your needs.\n\nI work alongside doctors of physical therapy and evidence-based research to support your recovery at home. While I’m not a doctor or clinician, I’m here to make this process easier and answer any questions you have.",

    // CTA response: "What's the Assessment?"
    whatsAssessment: "Here's what we'll do together — about 10 minutes total. Tap any step to learn more.",

    // Accordion step descriptions (for "What's the Assessment?" expanded view)
    assessmentSteps: [
      { title: "A few questions about your pain", body: "I'll ask where your pain started and a few targeted questions about what it feels like — sharp, dull, radiating. This helps me understand what's going on so I can pick the right movement checks for you." },
      { title: "A quick safety check", body: "Before any movements, I'll run through a short checklist to make sure it's safe to continue. If anything comes up, I'll recommend you see a provider first — your safety always comes first." },
      { title: "1–2 simple movement checks", body: "I'll pick 1 or 2 gentle movements based on your answers. I'll show you a demo video first, then guide you through it. The goal is never to push through pain — it's to help me understand your starting point." },
      { title: "A few optional personal details", body: "After the movement checks, you can share details like your age, activity level, and goals. The more I know, the more accurate your plan. If you skip this, I'll still build a plan with conservative assumptions." },
      { title: "Your personalized recovery plan", body: "Based on everything I've learned, I'll create a recovery plan with exercises tailored to you — including what to do, how often, and why each exercise was chosen." },
    ],

    // Inline intent responses (when user types freely at intro)
    areaDetected: "Thanks for sharing that. I'll keep that in mind as we go. Let me start by confirming where your pain began.",
    noAreaDetected: "Thanks for sharing that — I'll keep it in mind as we go through the assessment. Let me know when you're ready to start.",
    introOpenReminder: "This assessment helps me understand your pain so I can build a plan specific to you. It takes about 10 minutes. Let me know when you're ready.",
  },

  // ── 2. PROBLEM AREA ───────────────────────────────────────────
  problemArea: {
    prompt: "Can you start by telling me where you first felt your pain?",
    selectionConfirm: "{area} —  got it. Now let’s pinpoint where exactly you’re feeling this.",
    noSelectionPrompt: "Please make your selection before we can move to the next screen.",
  },

  // ── 3. PAIN LOCATION ──────────────────────────────────────────
  painLocation: {
    prompt: "Tap the buttons to mark all the areas on your body where you’re feeling pain, on one side or both.",
    confirmFew: "{zones} — got it. Now I need to do a quick safety check before we continue.",
    confirmMany: "Got it — {count} areas noted. Now I need to do a quick safety check before we continue.",
    noSelectionPrompt: "Please select at least one area before we can continue.",
  },

  // ── 4. SAFETY SCREEN ──────────────────────────────────────────
  safety: {
    prompt: "This guided assessment helps identify a starting point for your recovery. It is not a medical diagnosis and does not replace in-person care from a licensed healthcare provider. If you are experiencing a medical emergency, please call 911 or visit your nearest emergency room.",
    followUpIntro: "Thanks for letting me know. I have a couple of follow-up questions to make sure it's safe to continue.",
    followUpNext: "One more thing I need to check.",
    clearSeatedOnly: "Good news — we can continue, and I'll keep things to seated movements to be safe. Let me ask about the character of your pain.",
    clearNormal: "Good — we're clear to continue. Let me ask about the character of your pain.",
  },
  safetyOutcome: {
    advisoryHeading: "Something to consider before continuing",
    stoppedFollowUp: "I know this isn't the news you were hoping for. If you have any questions, I'm right here.",
    advisoryLeave: "No problem at all. When you're ready to give it a try, I'll be right here.",
  },

  // ── HARD STOP ─────────────────────────────────────────────────
  hardStop: {
    safety: {
      empathy: "Your safety is my top priority.",
      detail: "Based on what you've shared, I'd strongly recommend seeing a healthcare provider before starting an exercise program. They can evaluate your symptoms and let you know when it's safe to proceed.",
    },
    test1Pain: {
      empathy: "I'm glad you told me about the pain.",
      detail: "Experiencing pain during that movement tells me we should take a different approach. I'd recommend seeing a healthcare provider who can evaluate things in person and guide your recovery safely.",
    },
    couldntFinish: {
      empathy: "No worries at all — that's important information.",
      detail: "Not being able to complete the movement tells me this might need a more hands-on approach. I'd recommend seeing a healthcare provider who can work with you directly.",
    },
    followUp: "I know this isn't the outcome you were hoping for. If you have any questions about what this means or what to expect from an in-person visit, I'm here — just ask.",
  },

  // ── 5. CPG QUESTION ───────────────────────────────────────────
  cpg: {
    // LBP CPG prompts
    lbpLegQuestion: "Which of these best describes the feeling in your leg?",
    lbpBackQuestion: "Which of these best describes your main complaint?",
    // HKT CPG prompts come from CLINICAL_PATHS[area].cpgQuestion.prompt

    // Transition messages after CPG selection
    transitionToSubtype: "Thanks. As I continue to narrow things down, I have another question for you.",
    transitionToSubtypeLbp: "Thanks. Now let me narrow things down a bit more.",
    transitionToPreNprs: "Thanks. Let me ask about your pain level.",
    transitionToPreNprsFromBackOnly: "Thanks. Let me ask about your pain level.",
  },

  // ── 6. SUBTYPE Q1 (LBP) ──────────────────────────────────────
  subtypeQ1: {
    prompt: "Which posiion best describes your lower back pain?",
    promptWithPreselect: "Based on what you shared earlier, it sounds like this might apply — does it still sound right?",
    transitionToQ2: "One final question about this.",
  },

  // ── 7. SELF-ASSESSMENT Q2 (LBP) ──────────────────────────────
  q2: {
    // Q2 question text comes from Q2_MAP[q1Answer].title
    transitionToPreNprs: "Thanks — that helps narrow things down. Now let me ask about your pain level.",
  },

  // ── 7b. HKT SUBTYPE QUESTION ─────────────────────────────────
  hktSubtype: {
    // Prompt comes from CLINICAL_PATHS[area].subtypeQuestions[index].prompt
    transitionToPreNprs: "Thanks — that helps narrow things down. Let me ask about your pain level.",
  },

  // ── 8. PRE-NPRS (Pain + Duration) ────────────────────────────
  preNprs: {
    painPrompt: "How would you rate your pain right now? On a scale of 0 to 10, 0 means no pain, and 10 is the worst pain imaginable.",
    durationPromptDefault: "And how long have you been experiencing these symptoms?",
    durationPromptPreselect: "You mentioned how long this has been going on — does this match?",
    transitionToTest: "Thanks for sharing that with me! Based on what you’ve told me, I’ve chosen the {testName} as your first movement test. I’ll start by showing you a quick demo video.",
    voiceHeadsUp: "As we move into this next phase—watching the demo and trying the movement—I’ll guide you step by step with spoken instructions. Make sure your device is in a sturdy spot where you can see it, and turn the volume up since you’ll be at least a few feet away.",
  },

  // ── 9. MODE SELECTION (test-time communication preference) ──
  modeSelection: {
    prompt: /* PLACEHOLDER COPY */ "For the movement test, how would you like to communicate with me while you're away from your device?",
    talkLabel: "Talk (Voice) - Recommended",
    talkDesc: /* PLACEHOLDER COPY */ "Speak your responses out loud for a smoother experience. (Microphone permission required.)",
    chatLabel: "Chat (Type)",
    chatDesc: /* PLACEHOLDER COPY */ "Type your responses directly on your device. Keep in mind, you'll need to walk back to your device each time.",
  },

  // ── 9b. DEMO VIDEO / TEST INTRO ──────────────────────────────
  demoVideo: {
    // No explicit Myra message — video plays automatically
    // "I'm Ready" and "Skip this test" are button actions
  },

  // ── 9b. MOVEMENT TEST COMPLETION ─────────────────────────────
  movementTest: {
    completionSummary: /* PLACEHOLDER COPY */ "Great job — you completed the {testName}. Let me ask you a couple of follow-up questions.",
  },

  // ── 10. CAMERA PERMISSION ────────────────────────────────────
  camera: {
    prompt: "To help me do the best job assessing your movement during the test, I can use your camera to track your movements in real time. Would you like to enable your camera or prefer not to? Your video stream stays on your device and is never recorded.",
    cameraAllowed: "In the full app, the AR tracking screen would appear here. For this prototype, we'll simulate the camera path.",
    cameraDeclined: "No problem at all. I'll ask you a couple of quick questions after the movement instead.",
  },

  // ── 11. MOVEMENT (Camera path) ───────────────────────────────
  movement: {
    cameraGuide: "Go ahead and try the <strong>{testName}</strong> now. I'll be watching and counting your reps.",
  },

  // ── 12. SELF-REPORT MOVEMENT (No-camera path) ────────────────
  selfReportMovement: {
    guide: "Go ahead and try the <strong>{testName}</strong> now. Take your time — do about 5-6 repetitions at a comfortable pace. I'll ask you a couple of questions when you're done.",
  },

  // ── 13. SELF-REPORT ROM ──────────────────────────────────────
  selfReportRom: {
    // Heading/subheading come from SELF_REPORT_ROM constant
    prompt: "Thinking about your range of motion during the movement, did it...",
    transitionToTempo: "Thanks for sharing. One more question.",
  },

  // ── 14. SELF-REPORT TEMPO ────────────────────────────────────
  selfReportTempo: {
    // Heading/subheading come from SELF_REPORT_TEMPO constant
    // HARD_STOP outcome handled by hardStop.couldntFinish
    prompt: "Thinking about your pace during the movement, did you...",
    transitionToRpe: /* PLACEHOLDER COPY */ "Thanks — one more question about the movement.",
  },

  // ── 15. RPE ──────────────────────────────────────────────────
  rpe: {
    prompt: "How challenging did that movement feel?",
    transitionToFeelAffect: /* PLACEHOLDER COPY */ "Got it. One last question about how that movement affected your symptoms.",
  },

  // ── 16. FEEL AFFECT ──────────────────────────────────────────
  feelAffect: {
    prompt: "How did that movement make your symptoms feel?",

    // Override messages
    worsenedOverride: "Thanks for telling me — that's actually very useful information. It helps me adjust your classification. Let's move on to personalizing your plan.",
    rotationalImprovedOverride: "That's great feedback — it tells me a lot about what direction works for you. Let's move on.",

    // Normal flow
    noTest2: "That's all the movement testing we need. You're doing great — let's wrap things up.",
    transitionToTest2: "There's one more movement I'd like you to try — <strong>{testName}</strong>. Let me show you.",
  },

  // ── 17. TEST 2 SKIP ──────────────────────────────────────────
  test2: {
    skipConfirm: "No problem — we have plenty to work with from the first movement. Let's move on.",
  },

  // ── 18. FEEDBACK 2 ───────────────────────────────────────────
  feedback2: {
    prompt: "Compared to the first movement, how did this one make your symptoms feel?",
    transitionToPersonalize: "Great — that's really helpful. We're almost done.",
  },

  // ── 19. PERSONALIZE ──────────────────────────────────────────
  personalize: {
    // Group intros come from PERSONALIZE_GROUPS[n].myraIntro
    // Skip messages come from PERSONALIZE_SKIP_MESSAGES[groupId]
    skipAllConfirm: "No problem — I'll use conservative settings across the board. You can update these anytime.",
    finishTransition: "Alright — I'm calculating your Recovery Compass Score now.",
  },

  // ── 20. RECOVERY SCORE ───────────────────────────────────────
  recoveryScore: {
    // Score screen is rendered as inline UI, no explicit Myra chat message
    // Score explanation text is inline in the component
  },

  // ── 21. PLAN SCREEN ──────────────────────────────────────────
  plan: {
    intro: "Here’s your personalized Week 1 Recovery Plan! Based on your assessment, I’ve chosen exercises tailored to your needs to help you feel better and make progress safely. This is what you came here for—your path to recovery starts now!",
    qaEncouragement: "Have questions about your plan? Ask me anything—I’m here to help!",
  },

  // ── EXIT HANDLER ─────────────────────────────────────────────
  exit: {
    // Deterministic first messages per reason come from EXIT_MYRA_MESSAGES in CONSTANTS
    confirmExitGoodbye: "Thanks for giving the assessment a try. If you ever want to come back, I'll be here. Take care.",
  },
};


// ════════════════════════════════════════════════════════════════
// TEMPLATE VARIABLE RESOLVER
// ════════════════════════════════════════════════════════════════

export function resolveMessage(template, vars = {}) {
  return Object.entries(vars).reduce(
    (msg, [key, val]) => msg.replaceAll(`{${key}}`, val),
    template
  );
}
